// The table's inline name editing, in one state machine: renaming a file
// (stem only — the extension is shown but immutable), renaming a folder, and
// naming a just-created folder. At most one edit is active at a time, and
// each validates through the shared name rules with i18n'd messages.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extensionOf, type IDocument, type IFolder } from '@apis/documents';
import { siblingNamesOf, validateFileName, validateFolderName } from '../../utils/validation';
import { nameErrorMessage } from '../../utils/validationMessages';

interface IUseNameEditingArgs {
  documents: IDocument[];
  folders: IFolder[];
  siblingFolders: readonly IFolder[];
  onRenameDoc?: (id: string, newName: string) => void;
  onRenameFolder?: (id: string, newName: string) => void;
  onStartNewFolder?: () => boolean;
  onCommitNewFolder?: (name: string) => void;
  onCancelNewFolder?: () => void;
}

export function useNameEditing({
  documents, folders, siblingFolders, onRenameDoc, onRenameFolder,
  onStartNewFolder, onCommitNewFolder, onCancelNewFolder,
}: IUseNameEditingArgs) {
  const { t } = useTranslation();
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  // For a file this holds the stem only; `renameSuffix` carries the extension,
  // which is shown but not editable — it describes bytes a rename can't change.
  const [renameValue, setRenameValue] = useState('');
  const [renameSuffix, setRenameSuffix] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderError, setNewFolderError] = useState<string | null>(null);

  // Validated as the whole name, since that is what the API receives — the
  // extension counts towards length and character rules like the rest.
  function nameErrorOf(value: string): string | null {
    const err = renamingFolderId
      ? validateFolderName(value, siblingNamesOf(siblingFolders, renamingFolderId))
      : validateFileName(value + renameSuffix);
    return err ? nameErrorMessage(t, err) : null;
  }

  // A new folder is always a folder, so it can't use the doc-vs-folder branch above.
  function folderNameErrorOf(value: string): string | null {
    const err = validateFolderName(value, siblingNamesOf(siblingFolders, null));
    return err ? nameErrorMessage(t, err) : null;
  }

  function startRename(fileId: string) {
    const doc = documents.find((d) => d.id === fileId);
    const name = doc?.name ?? '';
    const ext = extensionOf(name);
    setRenameSuffix(ext);
    setRenameValue(name.slice(0, name.length - ext.length));
    setRenameError(null);
    setRenamingFolderId(null);
    setRenamingFileId(fileId);
  }

  function startRenameFolder(folderId: string) {
    const folder = folders.find((f) => f.id === folderId);
    const name = folder?.name ?? '';
    setRenameSuffix(''); // Folders have no extension — the whole name is editable.
    setRenameValue(name);
    setRenameError(null);
    setRenamingFileId(null);
    setRenamingFolderId(folderId);
  }

  function changeRename(value: string) {
    setRenameValue(value);
    if (renameError) setRenameError(value.trim() ? nameErrorOf(value) : null);
  }

  function cancelRename() {
    setRenamingFileId(null);
    setRenamingFolderId(null);
    setRenameError(null);
  }

  function commitRename() {
    if (!renamingFileId && !renamingFolderId) return;
    const trimmed = renameValue.trim();
    // An empty value (or a blurred new folder left untouched) just cancels.
    if (!trimmed) { cancelRename(); return; }
    const message = nameErrorOf(trimmed);
    if (message) { setRenameError(message); return; }
    // The extension is put back on: the user edited the stem, the API takes
    // the whole name.
    const nextName = renamingFileId ? trimmed + renameSuffix : trimmed;
    const currentName = renamingFileId
      ? documents.find((d) => d.id === renamingFileId)?.name
      : folders.find((f) => f.id === renamingFolderId)?.name;
    // An untouched name is a cancel, not a write. A blur — opening a menu,
    // clicking elsewhere — commits, and a rename to the same name is a real
    // request that re-paths the file and drops its cached bytes for nothing.
    if (nextName === currentName) { cancelRename(); return; }
    if (renamingFileId) onRenameDoc?.(renamingFileId, nextName);
    if (renamingFolderId) onRenameFolder?.(renamingFolderId, nextName);
    cancelRename();
  }

  function handleNewFolder() {
    if (!onStartNewFolder?.()) return;
    setNewFolderName(t('documents.contextMenu.newFolder'));
    setNewFolderError(null);
    setRenamingFileId(null);
    setRenamingFolderId(null);
  }

  function changeNewFolder(value: string) {
    setNewFolderName(value);
    if (newFolderError) setNewFolderError(value.trim() ? folderNameErrorOf(value) : null);
  }

  function commitNewFolder() {
    const trimmed = newFolderName.trim();
    if (!trimmed) { cancelNewFolder(); return; }
    const message = folderNameErrorOf(trimmed);
    if (message) { setNewFolderError(message); return; }
    setNewFolderError(null);
    onCommitNewFolder?.(trimmed);
  }

  function cancelNewFolder() {
    setNewFolderError(null);
    onCancelNewFolder?.();
  }

  return {
    renamingFileId, renamingFolderId, renameValue, renameSuffix, renameError,
    startRename, startRenameFolder, changeRename, commitRename, cancelRename,
    newFolderName, newFolderError,
    handleNewFolder, changeNewFolder, commitNewFolder, cancelNewFolder,
  };
}
